import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { GlassCard, PageLoader, StatusBadge } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Server, RefreshCw, Trash2, Loader2, FileText, HardDriveDownload } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function AdminInfrastructure() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [logs, setLogs] = useState(null);
  const [repair, setRepair] = useState(null);
  const load = () => api.get("/admin/infrastructure").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <PageLoader />;

  const reconcile = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: "refresh" }));
    try {
      const { data: r } = await api.post(`/admin/servers/${s.id}/reconcile`);
      toast.success(r.relaunched ? "Provisioning worker re-launched — refreshing…" : `Status: ${r.job_status || s.status}`);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy((b) => ({ ...b, [s.id]: null })); }
  };

  const viewLogs = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: "logs" }));
    try {
      const { data: r } = await api.get(`/admin/servers/${s.id}/provisioning`);
      setLogs({ server: s, ...r });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy((b) => ({ ...b, [s.id]: null })); }
  };

  const remove = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: "delete" }));
    try {
      const { data: r } = await api.post(`/admin/servers/${s.id}/delete`);
      if (r.ok) toast.success(`Removed ${r.hostname}`);
      else toast.warning(`Removed ${r.hostname} with issues: ${r.errors.join("; ")}`);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy((b) => ({ ...b, [s.id]: null })); }
  };

  const repairStorage = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: "repair" }));
    setRepair({ server: s, output: "Connecting to the server over SSH and repairing the B2 mount… this can take 1–2 minutes.", ok: null });
    try {
      await api.post(`/admin/servers/${s.id}/repair-storage`);
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: st } = await api.get(`/admin/servers/${s.id}/repair-status`);
        setRepair({ server: s, output: st.output || "Working…", ok: st.status === "done" ? st.ok : null });
        if (st.status === "done") {
          done = true;
          if (st.ok) toast.success("B2 mount repaired — datastore now backed by Backblaze");
          else toast.warning("Repair ran but the mount is still not active — see details");
        }
      }
      if (!done) toast.info("Repair is still running — reopen shortly to see the result.");
    } catch (e) { toast.error(apiErr(e)); setRepair(null); }
    finally { setBusy((b) => ({ ...b, [s.id]: null })); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2"><Server className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Infrastructure</h1></div>

      <div className="mb-5 flex flex-wrap gap-3">
        {data.versions.map((v) => (
          <div key={v.version} className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-2 text-sm">
            <span className="font-mono text-cyan-300">{v.version}</span> <span className="ml-2 text-slate-400">{v.count} servers</span>
          </div>
        ))}
      </div>

      <GlassCard className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-3">Customer</th><th className="p-3">Hostname</th><th className="p-3">Provider</th><th className="p-3">Region</th><th className="p-3">Status</th><th className="p-3">CPU/RAM</th><th className="p-3">Storage</th><th className="p-3">Cost</th><th className="p-3">Revenue</th><th className="p-3">Margin</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {data.servers.map((s) => (
              <tr key={s.id} className={`hover:bg-slate-900/40 ${s.unprofitable ? "bg-rose-500/5" : ""}`} data-testid={`infra-row-${s.id}`}>
                <td className="p-3 text-slate-200">{s.customer}</td>
                <td className="p-3 font-mono text-xs text-slate-400">{s.hostname}</td>
                <td className="p-3"><span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300">{s.provider}</span></td>
                <td className="p-3 text-slate-400">{s.region}</td>
                <td className="p-3">
                  <StatusBadge status={s.status} />
                  {s.provision_error && (s.status === "failed" || s.status === "action_required") && (
                    <div data-testid={`infra-error-${s.id}`} title={s.provision_error} className="mt-1 max-w-[240px] truncate text-xs text-rose-400/80">{s.provision_error}</div>
                  )}
                </td>
                <td className="p-3 font-mono text-xs text-slate-400">{s.cpu}% / {s.ram}%</td>
                <td className="p-3 font-mono text-slate-300">{s.storage_tb} TB</td>
                <td className="p-3 font-mono text-slate-400">${s.monthly_cost}</td>
                <td className="p-3 font-mono text-emerald-300">${s.monthly_revenue}</td>
                <td className={`p-3 font-mono font-semibold ${s.margin < 50 ? "text-rose-400" : "text-emerald-400"}`}>{s.margin}%</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      data-testid={`infra-logs-${s.id}`}
                      onClick={() => viewLogs(s)}
                      disabled={!!busy[s.id]}
                      title="View provisioning logs"
                      className="rounded-lg border border-slate-700 p-1.5 text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-40"
                    >
                      {busy[s.id] === "logs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    </button>
                    <button
                      data-testid={`infra-refresh-${s.id}`}
                      onClick={() => reconcile(s)}
                      disabled={!!busy[s.id]}
                      title="Update provisioning status"
                      className="rounded-lg border border-slate-700 p-1.5 text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-40"
                    >
                      {busy[s.id] === "refresh" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </button>
                    <button
                      data-testid={`infra-repair-${s.id}`}
                      onClick={() => repairStorage(s)}
                      disabled={!!busy[s.id]}
                      title="Repair B2 storage mount"
                      className="rounded-lg border border-slate-700 p-1.5 text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-40"
                    >
                      {busy[s.id] === "repair" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveDownload className="h-4 w-4" />}
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`infra-delete-${s.id}`}
                          disabled={!!busy[s.id]}
                          title="Remove infrastructure"
                          className="rounded-lg border border-slate-700 p-1.5 text-rose-400 transition-colors hover:border-rose-500/50 hover:bg-rose-500/10 disabled:opacity-40"
                        >
                          {busy[s.id] === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="infra-delete-dialog" className="border-slate-800 bg-slate-950">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-slate-50">Remove {s.hostname}?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            This permanently deletes the {s.provider === "hetzner" ? "Hetzner server, its volume and firewall, any B2 bucket," : "mock server resources"} and all database records for this server. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="infra-delete-cancel" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid="infra-delete-confirm" onClick={() => remove(s)} className="bg-rose-500 text-white hover:bg-rose-600">Remove permanently</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>
      <p className="mt-3 text-xs text-slate-500">Rows highlighted in red indicate accounts below the 50% gross-margin floor. Use <FileText className="inline h-3 w-3" /> for logs, <RefreshCw className="inline h-3 w-3" /> to update provisioning status, <Trash2 className="inline h-3 w-3 text-rose-400" /> to remove infrastructure.</p>

      <Dialog open={!!logs} onOpenChange={(v) => !v && setLogs(null)}>
        <DialogContent className="max-w-2xl border-slate-800 bg-[#0D111A]" data-testid="infra-logs-dialog">
          {logs && (
            <>
              <DialogHeader>
                <DialogTitle className="text-slate-100">Provisioning logs · {logs.server.hostname}</DialogTitle>
                <DialogDescription className="text-slate-500">
                  Status: <span className="text-slate-300">{logs.job_status || logs.server_status || "—"}</span>
                  {logs.current_step ? ` · ${logs.current_step}` : ""}
                </DialogDescription>
              </DialogHeader>
              {logs.provision_error && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-2 font-mono text-xs text-rose-300">{logs.provision_error}</div>
              )}
              <div data-testid="infra-logs-body" className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-800 bg-black/50 p-3 font-mono text-[11px] leading-relaxed">
                {(!logs.logs || logs.logs.length === 0) && <div className="text-slate-500">No logs yet. Logs appear for LIVE provisioning as each step runs.</div>}
                {(logs.logs || []).map((l, i) => (
                  <div key={i} className={l.level === "error" ? "text-rose-400" : l.level === "warn" ? "text-amber-300" : "text-slate-400"}>
                    <span className="text-slate-600">{new Date(l.ts).toLocaleTimeString()} </span>{l.message}
                  </div>
                ))}
              </div>
              <button onClick={() => viewLogs(logs.server)} data-testid="infra-logs-refresh" className="inline-flex items-center gap-2 self-start rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:border-cyan-500/50"><RefreshCw className="h-4 w-4" /> Refresh logs</button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!repair} onOpenChange={(v) => !v && setRepair(null)}>
        <DialogContent className="max-w-2xl border-slate-800 bg-[#0D111A]" data-testid="infra-repair-dialog">
          {repair && (
            <>
              <DialogHeader>
                <DialogTitle className="text-slate-100">B2 storage repair · {repair.server.hostname}</DialogTitle>
                <DialogDescription className="text-slate-500">
                  {repair.ok === null ? "Running…" : repair.ok
                    ? "Mount active — the PBS datastore is now backed by Backblaze B2."
                    : "The mount is still not active. Review the diagnostics below."}
                </DialogDescription>
              </DialogHeader>
              <pre data-testid="infra-repair-body" className="max-h-[55vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-800 bg-black/50 p-3 font-mono text-[11px] leading-relaxed text-slate-300">
                {repair.output}
              </pre>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
